import { bdTrackerAllowedRoles } from 'data/bdTracker/bdTrackerUserRoleValidations';
import { Source, Stage, Status, User } from 'Interfaces/BDPortalInterfaces';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Avatar } from 'primereact/avatar';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import 'react-quill/dist/quill.snow.css';
import { getBackgroundColor } from 'utils/avatarUtilities';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import { getInitials } from 'utils/getInitials';
import EditOpportunity from '../EditOpportunity/EditOpportunity';
import OpportunityFilter from '../OpportunityFilter/OpportunityFilter';
import styles from './OpportunitKanbanLIst.module.scss';
import { OpportunityKanbanBoardLogic } from './OpportunityKanbanBoard.logic';
import './test.scss';
import { colorCodingData } from 'data/bdTracker/IterationsColorCodingData';
import { getStatus } from 'utils/bdTrackerUtilities/checkStageUtility';

const MultiKanbanBoard: React.FC = () => {
  const {
    userModulesRoles,
    isOpportunityBoardDetailsLoading,
    boardDetails,
    handleSetItertation,
    iteration,
    opportunityIID,
    setUpdateBoardPayload,
    updateBoardPayload,
    isEditOpportunityModalOpen,
    closeEditOpportunityModal,
    handleListButtonClick,
    expandedStageNames,
    onDragEnd,
    board,
    filterAnalyst,
    allOpportunityDetails,
    searchKeyword,
    toggleBoardExpansion,
    handleCardClick,
    updateBoard,
    assigneeUpdatingCards,
    quickLoadCardsLocal,
    opportunityID,
    quickLoad,
    checkIfCardIsGettingAssignedUpdated,
    filterOpportunityIterationsForTheappliedFilters,
  } = OpportunityKanbanBoardLogic();

  const opportunity_details = toJS(allOpportunityDetails);

  useEffect(() => {}, [filterAnalyst, allOpportunityDetails, searchKeyword]);

  const assigneeOptionTemplate = (option: User | null) => {
    return (
      <>
        {option ? (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div style={{ width: '3rem', height: '100%' }}>
              <Avatar
                title={option.Name}
                id="profileAvatar"
                shape="circle"
                label={option.Name && getInitials(option?.Name)}
                style={{
                  backgroundColor: getBackgroundColor(option.Name[0]),
                  color: '#522D6E',
                  marginRight: '0.5rem',
                }}
              />
            </div>
            <div>{option.Name}</div>
          </div>
        ) : (
          <span>Unassigned</span>
        )}
      </>
    );
  };

  const KanbanLoader = () => {
    return (
      <div style={{ padding: '0 1rem' }}>
        <Skeleton height="3rem" width="100%"></Skeleton>
        <div style={{ display: 'flex', gap: '1.5rem', margin: '1rem 0' }}>
          <Skeleton height="25rem" style={{ flex: 1 }}></Skeleton>
          <Skeleton height="25rem" style={{ flex: 1 }}></Skeleton>
          <Skeleton height="25rem" style={{ flex: 1 }}></Skeleton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
          <Skeleton height="3rem" width="100%"></Skeleton>
        </div>
      </div>
    );
  };

  return (
    <div className="bdportal h-screen flex flex-col p-4">
      <OpportunityFilter
        boardType={iteration}
        setBoardType={handleSetItertation}
        viewType={'Board'}
        handleListClick={handleListButtonClick}
      />

      {/* Fix for the loader not getting disabled in the draggable cards */}
      {quickLoadCardsLocal.size ? '' : ''}
      {assigneeUpdatingCards.size ? '' : ''}

      {!isOpportunityBoardDetailsLoading ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="bdportal flex-1 p-4 overflow-auto">
            {(board === 'NC' || board === 'MAS') &&
              boardDetails?.[board]?.Stage?.map((stage: Stage) => (
                <div key={stage.Stage} className="bdportal mb-4">
                  {/* Stage Header */}
                  <button
                    onClick={() => toggleBoardExpansion(stage.Stage)}
                    className="bdportal w-full text-left font-bold p-4 rounded flex items-center text-white" style={{ background: 'linear-gradient(90deg, #31174C, #602E93)' }}
                  >
                    <span>{stage.Stage}</span>
                    <span
                      style={
                        expandedStageNames.includes(stage.Stage)
                          ? {
                              display: 'flex',
                              marginLeft: '0.5rem',
                              transitionDuration: '0.5s',
                            }
                          : {
                              rotate: '90deg',
                              marginLeft: '0.5rem',
                              transitionDuration: '0.5s',
                            }
                      }
                    >
                      ►
                    </span>
                  </button>
                  {/* Stage Content */}
                  {/* {expandedStageNames.includes(stage.Stage) && ( */}
                  {expandedStageNames.includes(stage.Stage) && (
                    <div
                      style={
                        expandedStageNames.includes(stage.Stage)
                          ? {
                              display: 'flex',
                              height: '400px',
                              marginTop: '1rem',
                              transitionDuration: '0.5s',
                              overflow: 'hidden',
                            }
                          : {
                              height: 0,
                              transitionDuration: '0.5s',
                              overflow: 'hidden',
                            }
                      }
                    >
                      <div
                        style={{
                          display: 'flex',
                          height: '400px',
                          width: '100%',
                        }}
                      >
                        <>
                          {getStatus(stage.ID, boardDetails?.[board]?.Status)?.map(
                            (status: Status) => (
                              <div
                                key={`${stage.Stage}|${status.Status}`}
                                className="bdportal flex-1 px-2"
                              >
                                {/* Status Header */}
                                <h3 className="bdportal font-bold mb-2">{status.Status}</h3>
                                {/* Droppable Area */}
                                {expandedStageNames.includes(stage.Stage) && (
                                  <Droppable droppableId={`${stage.ID}|${status.ID}`}>
                                    {provided => (
                                      <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="bdportal bg-theme-card border-theme-primary p-2 rounded overflow-y-auto"
                                        style={{ height: 'calc(100% - 30px)' }}
                                      >
                                        {/* Draggable Opportunities */}
                                        {filterOpportunityIterationsForTheappliedFilters(
                                          opportunity_details,
                                          stage.ID,
                                          status.ID
                                        ).map((opportunity, index) => {
                                          const draggableId = String(
                                            opportunity.OpportunityIterationsID
                                          );
                                          return (
                                            <Draggable
                                              key={opportunity.OpportunityIterationsID}
                                              draggableId={draggableId}
                                              index={index}
                                              // drag should get disabled when opportunity is being updated
                                              isDragDisabled={
                                                quickLoadCardsLocal.has(
                                                  opportunity.OpportunityIterationsID
                                                ) ||
                                                checkIfCardIsGettingAssignedUpdated(
                                                  opportunity.OpportunityIterationsID
                                                )
                                              }
                                            >
                                              {provided => {
                                                const source = boardDetails?.[board]?.Source?.find(
                                                  (src: Source) => src.ID === opportunity.SourceID
                                                )?.Source;
                                                return (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={`bdportal bg-theme-primary border-theme-primary relative p-2 mb-2 rounded shadow transition-colors duration-200 relative`}
                                                    style={{
                                                      ...provided.draggableProps.style,
                                                      width: '23rem',
                                                      paddingLeft: '1rem',
                                                      borderWidth: '1px'
                                                    }}
                                                  >
                                                    {opportunity.PortfolioCounter !== null && (
                                                      <div
                                                        className="bdportal text-sm text-gray-600 mb-1"
                                                        style={{
                                                          backgroundColor: '#FFEAB7',
                                                          color: '#522D6E',
                                                          textAlign: 'center',
                                                          padding: '0.2rem',
                                                          position: 'absolute',
                                                          top: 0,
                                                          left: 0,
                                                          right: 0,
                                                          fontWeight: 'bold',
                                                        }}
                                                      >
                                                        Portfolio
                                                      </div>
                                                    )}
                                                    <div
                                                      style={{
                                                        backgroundColor:
                                                          colorCodingData[
                                                            Number(
                                                              (opportunity.PortfolioCounter
                                                                ? opportunity.PortfolioCounter
                                                                : opportunity.IterationCounter ||
                                                                  20) % 50
                                                            ) || 20
                                                          ],
                                                        width: '0.3rem',
                                                        height: '100%',
                                                        position: 'absolute',
                                                        top: 0,
                                                        bottom: 0,
                                                        left: 0,
                                                      }}
                                                    ></div>

                                                    {quickLoadCardsLocal.has(
                                                      opportunity.OpportunityIterationsID
                                                    ) && (
                                                      <div
                                                        style={{
                                                          position: 'absolute',
                                                          inset: 0,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          backgroundColor: 'white',
                                                          opacity: 0.8,
                                                          zIndex: 10,
                                                          flexGrow: 1,
                                                        }}
                                                      >
                                                        <ProgressSpinner
                                                          style={{
                                                            width: '30px',
                                                            height: '30px',
                                                          }}
                                                          strokeWidth="8"
                                                          fill="var(--surface-ground)"
                                                          animationDuration=".5s"
                                                        />
                                                      </div>
                                                    )}

                                                    <div
                                                      className={`bdportal font-semibold flex items-center text-theme-primary p-1`}
                                                      style={{
                                                        gap: '1rem',
                                                        marginTop:
                                                          opportunity.PortfolioCounter != null
                                                            ? '1.5rem'
                                                            : 'auto',
                                                      }}
                                                      onClick={() =>
                                                        handleCardClick(
                                                          opportunity.OpportunityIterationsID,
                                                          opportunity.OpportunityID
                                                        )
                                                      }
                                                    >
                                                      <span>
                                                        {opportunity.IterationCounter
                                                          ? opportunity.IterationCounter
                                                          : '-'}
                                                      </span>
                                                      <span
                                                        style={{
                                                          textDecoration: 'none',
                                                          wordBreak: 'break-word',
                                                        }}
                                                        onMouseOver={e =>
                                                          ((
                                                            e.target as HTMLSpanElement
                                                          ).style.textDecoration = 'underline')
                                                        }
                                                        onMouseOut={e =>
                                                          ((
                                                            e.target as HTMLSpanElement
                                                          ).style.textDecoration = 'none')
                                                        }
                                                      >
                                                        {opportunity.MoleculeAndForm}
                                                      </span>
                                                    </div>
                                                    <div className="bdportal text-sm text-gray-600 mb-1">
                                                      {source}
                                                    </div>
                                                    {/* <br /> */}
                                                    <div className={styles.dropdown}>
                                                      <Dropdown
                                                        valueTemplate={assigneeOptionTemplate}
                                                        itemTemplate={assigneeOptionTemplate}
                                                        options={boardDetails?.[board].User}
                                                        optionLabel="Name"
                                                        optionValue="ID"
                                                        value={opportunity.AnalystID}
                                                        onClick={e => e.stopPropagation()}
                                                        onChange={e => {
                                                          updateBoard(
                                                            opportunity.StageID,
                                                            opportunity.StatusID,
                                                            e.value,
                                                            opportunity.OpportunityIterationsID
                                                          );
                                                          setUpdateBoardPayload({
                                                            ...updateBoardPayload,
                                                            AnalystID: e.value,
                                                          });
                                                        }}
                                                        placeholder="Unassigned"
                                                        className={styles.selectDropdown}
                                                        style={{
                                                          width: '100%',
                                                          border: '1px solid #cbcbcb',
                                                          borderRadius: '0.25rem',
                                                        }}
                                                        loading={checkIfCardIsGettingAssignedUpdated(
                                                          opportunity.OpportunityIterationsID
                                                        )}
                                                        disabled={
                                                          !checkIfFunctionalityEnabled(
                                                            bdTrackerAllowedRoles.cardAssigneeChangingDropdown,
                                                            userModulesRoles.opptracker,
                                                            userModulesRoles
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                );
                                              }}
                                            </Draggable>
                                          );
                                        })}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                )}
                              </div>
                            )
                          )}{' '}
                        </>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </DragDropContext>
      ) : !quickLoad ? (
        <KanbanLoader />
      ) : null}

      {/* Existing Modals remain the same */}
      {isEditOpportunityModalOpen && opportunityIID && (
        <EditOpportunity
          visible={isEditOpportunityModalOpen}
          closeModal={closeEditOpportunityModal}
          viewType={'Board'}
          opportunityIterationID={opportunityIID}
          opportunityID={opportunityID!}
        />
      )}
    </div>
  );
};

export default observer(MultiKanbanBoard);
